// Work Collaboration Hub (v28) — the command operations board.
//
// One place to see and reach every collaboration lane that already
// exists (v17–v27). This is a ROUTING SURFACE over those lanes with
// honest per-lane status from /api/square/work-collaboration/hub —
// it owns no engine of its own and never duplicates one.
//
// Presentation: the QUAM-style workspace frame. Each lane is a
// WorkspaceModuleCard with its real status chip; the selected-lane
// detail, governance routes (approval queue + receipts), and the
// live SIGNAL rail sit in the right rail; the "Prepare action"
// composer routes into the v23 approval queue or to the owning
// surface — never to an external provider. Square Code-native:
// no Microsoft Teams, Slack, monday.com, or GitLab bridges exist
// in this lane.

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Banner,
  EmptyState,
  InlineAlert,
  WorkspaceCommandShell,
  WorkspaceModuleCard,
  WorkspaceNotificationRail,
  WorkspaceRightRail,
  WorkspaceRailSection,
  WorkspaceRailChips,
  WorkspaceActionComposer,
  WorkspaceStatusChip,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_TEXT,
  workspaceTint,
} from '@square/ui'
import { api, ApiError, isAuthError } from '../../../api/client'
import { useWorkspaceSignals } from '../../../hooks/useWorkspaceSignals'

const ACCENT = WORKSPACE_ACCENTS.orange

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

const fieldStyle = {
  padding: '8px 10px',
  fontSize: 12.5,
  background: 'rgba(255,255,255,0.03)',
  color: WORKSPACE_TEXT.primary,
  border: `1px solid ${workspaceTint(ACCENT, 14)}`,
  borderRadius: 8,
  outlineColor: ACCENT,
  fontFamily: WORKSPACE_FONT.ui,
  minWidth: 0,
} as const

type HubModule = {
  key: string; label: string; owningLane: string; routePath: string
  summary: string; status: string; statusReason: string
}
type Prepared = { kind: string; title: string; status: string; route_path?: string; action_request_id?: string; note?: string }

// Lane glyphs — identity only. Status truth stays in the chip, which
// resolves through the shared workspace vocabulary (ready /
// requires_setup / draft_only / blocked / coming_later).
const MODULE_GLYPHS: Record<string, string> = {
  team_workspace: '◈',
  project_board: '▦',
  client_ops: '◍',
  agent_workspace: '⬡',
  secure_access: '⛨',
  meeting_mode: '◉',
  calendar: '▤',
  receipts: '▢',
  approvals: '❖',
  files: '▣',
}

const PREPARE_KINDS = [
  { value: 'draft_meeting', label: 'Meeting (approval queue)' },
  { value: 'draft_secure_access', label: 'Secure access (approval queue)' },
  { value: 'draft_message', label: 'Team message (owning surface)' },
  { value: 'draft_board_update', label: 'Board update (owning surface)' },
  { value: 'draft_calendar_event', label: 'Calendar event (owning surface)' },
  { value: 'draft_agent_task', label: 'Agent task (owning surface)' },
  { value: 'draft_client_update', label: 'Client update (v25, coming later)' },
]
const CONTEXT_TYPES = ['personal', 'project', 'client', 'team', 'workspace']

// Small accent link-button used in the rail (existing targets only).
function RailLink({ label, accent, onClick }: { label: string; accent: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 6,
        padding: '5px 10px',
        borderRadius: 7,
        cursor: 'pointer',
        background: workspaceTint(accent, 8),
        border: `1px solid ${workspaceTint(accent, 18)}`,
        color: accent,
        fontSize: 9.5,
        fontWeight: 700,
        fontFamily: WORKSPACE_FONT.mono,
        letterSpacing: '0.08em',
      }}
    >
      {label}
    </button>
  )
}

export default function WorkCollaborationHub() {
  const navigate = useNavigate()
  const signals = useWorkspaceSignals({ limit: 8 })
  const [modules, setModules] = useState<HubModule[] | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [selected, setSelected] = useState<HubModule | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [busy, setBusy] = useState(false)

  const [kind, setKind] = useState('draft_meeting')
  const [contextType, setContextType] = useState('personal')
  const [contextId, setContextId] = useState('')
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [prepared, setPrepared] = useState<Prepared | null>(null)

  const refresh = useCallback(async () => {
    try {
      const result = await api.workCollaboration.hub()
      setModules(result.hub.modules)
      setWarnings(result.hub.warnings)
    } catch (err) {
      if (isAuthError(err)) { setAuthRequired(true); return }
      setError(formatError(err))
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const prepare = useCallback(async () => {
    setBusy(true)
    setError(null)
    setPrepared(null)
    try {
      const result = await api.workCollaboration.prepare({
        kind, title: title.trim(), summary: summary.trim() || undefined,
        context_type: contextType, context_id: contextId.trim() || undefined,
      })
      if (result.prepared) {
        setPrepared(result.prepared)
        setTitle('')
        setSummary('')
      }
    } catch (err) {
      if (isAuthError(err)) { setAuthRequired(true); return }
      setError(formatError(err))
    } finally { setBusy(false) }
  }, [kind, title, summary, contextType, contextId])

  const readyCount = modules?.filter((m) => m.status === 'ready').length ?? 0

  // Header chip is derived from real state only: the hub GET succeeded
  // (powered), failed (error), or has not resolved yet (no chip). A
  // routing board that has not loaded is not "ready".
  const headerStatus = error ? 'error' : modules !== null ? 'powered' : undefined

  // The prepared receipt reads through the honest vocabulary:
  // approval_pending → PREPARED (gold), coming_later → COMING LATER,
  // anything else falls back to muted uppercase — never a fake sent.
  const preparedChipStatus = prepared
    ? (prepared.status === 'approval_pending' ? 'prepared' : prepared.status)
    : null

  return (
    <WorkspaceCommandShell
      kicker="SQUARE CODE · WORK OPERATIONS"
      title="Work Collaboration"
      subtitle="One hub for every collaboration lane — team messages, boards, agents, meetings, secure access, calendar, receipts, and approvals. It routes to the existing lanes and does not duplicate their engines. No external provider bridges: no Microsoft Teams, Slack, monday.com, or GitLab dispatch exists here."
      accent={ACCENT}
      status={headerStatus}
      chips={modules !== null && modules.length > 0 ? (
        <WorkspaceStatusChip status="info" label={`${readyCount}/${modules.length} lanes ready`} />
      ) : undefined}
      actions={<Button variant="secondary" onClick={() => void refresh()}>Refresh</Button>}
      rail={
        <WorkspaceRightRail>
          <WorkspaceRailSection label="SELECTED LANE" accent={ACCENT}>
            {selected ? (
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: workspaceTint(ACCENT, 6),
                  border: `1px solid ${workspaceTint(ACCENT, 14)}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: WORKSPACE_TEXT.primary, fontFamily: WORKSPACE_FONT.ui }}>
                    {selected.label}
                  </span>
                  <WorkspaceStatusChip status={selected.status} size="sm" />
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.6, color: WORKSPACE_TEXT.secondary, fontFamily: WORKSPACE_FONT.ui }}>
                  {selected.statusReason}
                </div>
                <div style={{ fontSize: 10, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.mono, letterSpacing: '0.04em' }}>
                  {selected.owningLane}
                </div>
                {selected.status !== 'coming_later' && (
                  <div>
                    <Button size="small" onClick={() => navigate(selected.routePath)}>Open lane</Button>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px dashed var(--sc-workspace-line, rgba(255,255,255,0.07))',
                  fontSize: 11,
                  lineHeight: 1.6,
                  color: WORKSPACE_TEXT.muted,
                  fontFamily: WORKSPACE_FONT.ui,
                }}
              >
                Pick a lane card to see its honest status detail — the reason, the owning lane, and where it routes.
              </div>
            )}
          </WorkspaceRailSection>

          <WorkspaceRailSection label="GOVERNANCE" accent={WORKSPACE_ACCENTS.purple}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <RailLink
                label="APPROVAL QUEUE →"
                accent={WORKSPACE_ACCENTS.purple}
                onClick={() => navigate('/square/command-center/agent-workspace')}
              />
              <RailLink
                label="OLIVIUM RECEIPTS →"
                accent={WORKSPACE_ACCENTS.gold}
                onClick={() => navigate('/square/olivium/receipts')}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <WorkspaceRailChips
                items={[
                  { label: 'v23 approval queue', accent: WORKSPACE_ACCENTS.purple },
                  { label: 'olv audit trail', accent: WORKSPACE_ACCENTS.gold },
                ]}
              />
            </div>
          </WorkspaceRailSection>

          <WorkspaceRailSection label="SIGNAL">
            <WorkspaceNotificationRail
              title="LIVE"
              items={signals.items}
              channels={signals.channels}
              loading={signals.loading}
              live={signals.live}
              compact
              maxItems={6}
              filterable={false}
              sourceLabel={signals.sourceLabel}
              emptyTitle="No signal yet"
              emptyBody="Live operator signal (activation, gates, receipts) lands here."
            />
          </WorkspaceRailSection>
        </WorkspaceRightRail>
      }
      composer={authRequired ? undefined : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 9,
                letterSpacing: '0.26em',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: workspaceTint(ACCENT, 75),
                fontFamily: WORKSPACE_FONT.mono,
                whiteSpace: 'nowrap',
              }}
            >
              Prepare action
            </span>
            <span style={{ fontSize: 11, lineHeight: 1.5, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.ui }}>
              Prepared only — meeting and secure-access requests land in the approval queue and nothing executes before you approve. Other kinds route you to the owning surface.
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 6 }}>
            <select aria-label="Action kind" value={kind} onChange={(e) => setKind(e.target.value)} style={fieldStyle}>
              {PREPARE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            <select aria-label="Context type" value={contextType} onChange={(e) => setContextType(e.target.value)} style={fieldStyle}>
              {CONTEXT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input aria-label="Context id (optional)" value={contextId} onChange={(e) => setContextId(e.target.value)} placeholder="Context id…" style={fieldStyle} />
            <input aria-label="Action title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What should be prepared?" style={fieldStyle} />
          </div>
          <WorkspaceActionComposer
            accent={ACCENT}
            value={summary}
            onChange={setSummary}
            onSubmit={title.trim() ? () => void prepare() : undefined}
            multiline
            placeholder="Explain what will happen (shown in the approval queue)…"
            submitLabel="Prepare"
            busyLabel="Preparing"
            busy={busy}
            gateLabel="Prepared, never auto-executed — the operator decision happens in the approval queue"
            hint={title.trim() ? '↵ prepares' : 'Add an action title to enable Prepare'}
          />
          {prepared && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {preparedChipStatus && <WorkspaceStatusChip status={preparedChipStatus} size="sm" />}
                <span style={{ fontSize: 11, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.mono }}>
                  {prepared.title || prepared.kind}
                </span>
              </div>
              <InlineAlert severity={prepared.status === 'coming_later' ? 'info' : 'success'}>
                {prepared.status === 'approval_pending' && 'Prepared and waiting in the approval queue. '}
                {prepared.status === 'routed_to_owning_surface' && 'This action belongs to another surface. '}
                {prepared.note}
              </InlineAlert>
              {prepared.route_path && (
                <div>
                  <Button size="small" variant="secondary" onClick={() => navigate(prepared.route_path as string)}>
                    {prepared.status === 'approval_pending' ? 'Open approval queue' : 'Go to owning surface'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    >
      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="warning" title="Work Collaboration">{error}</Banner>
        </div>
      )}

      {authRequired ? (
        <EmptyState message="Sign in to continue." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {warnings.length > 0 && (
            <InlineAlert severity="info">{warnings.join(' ')}</InlineAlert>
          )}
          {modules === null ? (
            <EmptyState message="Loading collaboration modules…" />
          ) : modules.length === 0 ? (
            <div>
              <EmptyState message="The hub route returned no collaboration lanes — nothing is hidden, there is just nothing to show yet." />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="secondary" onClick={() => void refresh()}>Refresh lanes</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
              {modules.map((m) => (
                <WorkspaceModuleCard
                  key={m.key}
                  glyph={MODULE_GLYPHS[m.key] ?? '◆'}
                  title={m.label}
                  status={m.status}
                  accent={ACCENT}
                  summary={m.summary}
                  footnote={m.owningLane}
                  onOpen={m.status !== 'coming_later' ? () => navigate(m.routePath) : undefined}
                  actions={
                    <Button size="small" variant="secondary" onClick={() => setSelected(m)}>Details</Button>
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </WorkspaceCommandShell>
  )
}
